import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '/api';

export const useMovements = () => {
  const [movements, setMovements] = useState([]);
  const [stats, setStats] = useState({
    totalSales: 0,
    totalIncome: 0,
    avgTicket: 0,
    salesByService: {},
    salesByClient: {},
    totalMovements: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showMovementForm, setShowMovementForm] = useState(false);
  const [newMovement, setNewMovement] = useState({ 
    date: new Date().toISOString().slice(0, 10), 
    type: 'venta', 
    amount: 0, 
    clientId: 1, 
    serviceId: 1, 
    description: '' 
  });
  const [monthFilter, setMonthFilter] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [typeFilter, setTypeFilter] = useState('all');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });
  
  const { csrfToken, refreshCsrfToken } = useAuth();

  const ensureFreshCsrfToken = async () => {
    try {
      const result = await refreshCsrfToken();
      if (!result.success) {
        console.error('Error al refrescar el token CSRF');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error al refrescar el token CSRF:', error);
      return false;
    }
  };

  const fetchMovements = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      
      // Construir parámetros de consulta para movimientos (con paginación)
      const movementsParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (monthFilter) movementsParams.append('month', monthFilter);
      if (yearFilter) movementsParams.append('year', yearFilter);
      if (typeFilter && typeFilter !== 'all') movementsParams.append('type', typeFilter);
      
      // Construir parámetros para estadísticas (SIN paginación)
      const statsParams = new URLSearchParams();
      if (monthFilter) statsParams.append('month', monthFilter);
      if (yearFilter) statsParams.append('year', yearFilter);
      if (typeFilter && typeFilter !== 'all') statsParams.append('type', typeFilter);
      
      // Cargar movimientos primero
      const movementsResponse = await axios.get(`${API_URL}/movements?${movementsParams}`);
      setMovements(movementsResponse.data.movements);
      setPagination(movementsResponse.data.pagination);
      
      // Intentar cargar estadísticas completas del servidor
      try {
        const statsResponse = await axios.get(`${API_URL}/movements/stats?${statsParams}`);
        setStats(statsResponse.data);
        console.log('Estadísticas cargadas del servidor:', statsResponse.data);
      } catch (statsError) {
        console.warn('Error al cargar estadísticas del servidor, usando fallback:', statsError);
        
        // Fallback: calcular estadísticas básicas solo de la página actual
        const movements = movementsResponse.data.movements || [];
        const fallbackStats = {
          totalSales: movements.filter(m => m.type === 'venta').length,
          totalIncome: movements
            .filter(m => m.type === 'venta')
            .reduce((sum, m) => sum + parseFloat(m.amount || 0), 0),
          avgTicket: 0,
          salesByService: {},
          salesByClient: {},
          totalMovements: movements.length
        };
        
        if (fallbackStats.totalSales > 0) {
          fallbackStats.avgTicket = fallbackStats.totalIncome / fallbackStats.totalSales;
        }
        
        setStats(fallbackStats);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error al cargar movimientos:', err);
      setError('Error al cargar los movimientos. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMovement = async () => {
    try {
      setLoading(true);
      
      if (!await ensureFreshCsrfToken()) {
        setError('Error al obtener un token CSRF válido. Intente recargar la página.');
        setLoading(false);
        return;
      }
      
      const movementData = {
        date: newMovement.date,
        type: newMovement.type,
        amount: parseFloat(newMovement.amount),
        clientId: parseInt(newMovement.clientId),
        serviceId: parseInt(newMovement.serviceId),
        description: newMovement.description
      };

      if (newMovement.id) {
        await axios.put(`${API_URL}/movements/${newMovement.id}`, movementData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      } else {
        await axios.post(`${API_URL}/movements`, movementData, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });
      }

      await fetchMovements(pagination.currentPage, pagination.limit);
      
      setNewMovement({ 
        date: new Date().toISOString().slice(0, 10), 
        type: 'venta', 
        amount: 0, 
        clientId: '', 
        serviceId: '', 
        description: '' 
      });
      setShowMovementForm(false);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Error al guardar el movimiento. Por favor, intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMovement = async (movementId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este movimiento?')) {
      try {
        setLoading(true);
        
        if (!await ensureFreshCsrfToken()) {
          setError('Error al obtener un token CSRF válido. Intente recargar la página.');
          setLoading(false);
          return;
        }
        
        await axios.delete(`${API_URL}/movements/${movementId}`, {
          headers: {
            'X-CSRF-Token': csrfToken
          }
        });

        await fetchMovements(pagination.currentPage, pagination.limit);
        setError(null);
      } catch (err) {
        console.error('Error:', err);
        setError('Error al eliminar el movimiento. Por favor, intente de nuevo.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditMovement = (movement) => {
    setNewMovement({
      id: movement.id,
      date: new Date(movement.date).toISOString().slice(0, 10),
      type: movement.type,
      amount: movement.amount,
      clientId: movement.client_id || movement.clientId,
      serviceId: movement.service_id || movement.serviceId,
      description: movement.description
    });
    setShowMovementForm(true);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    fetchMovements(newPage, pagination.limit);
  };

  const handleFilterChange = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchMovements(1, pagination.limit);
  };

  const calculateSalesStats = () => {
    // Ahora devolvemos las estadísticas del servidor
    return stats;
  };

  const calculateTotalSales = () => {
    // Ahora devolvemos el total de ventas del servidor
    return stats.totalIncome || 0;
  };

  return {
    movements,
    loading,
    error,
    setError,
    showMovementForm,
    setShowMovementForm,
    newMovement,
    setNewMovement,
    monthFilter,
    setMonthFilter,
    yearFilter,
    setYearFilter,
    typeFilter,
    setTypeFilter,
    pagination,
    fetchMovements,
    handleSaveMovement,
    handleDeleteMovement,
    handleEditMovement,
    handlePageChange,
    handleFilterChange,
    calculateSalesStats,
    calculateTotalSales
  };
};
